import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Form,
  Input,
  Button,
  message,
  Upload,
  Modal,
  Alert,
  Divider,
} from "antd";
import { UploadOutlined, InboxOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useAuth } from "../contexts/AuthContext";
import ConfirmationModal from "./ConfirmationModal";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const DashboardStyle = styled.div`
  margin-left: 280px;
  overflow-x: hidden;
`;

const CardHeader = styled.h4`
  font-weight: bold;
`;

const ListItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
  padding: 5px 0;
`;

const InviteButton = styled(Button)`
  margin-left: 12px;
  border: 2px solid #3c2f61;
  color: #3c2f61;
  background-color: white;
`;

const HorizontalLine = styled.div`
  border-top: 1px solid #ccc;
  margin: 5px 0;
`;

const UploadArea = styled.div`
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  background-color: #fafafa;
  margin: 10px 0;
`;

const SkippedEmailItem = styled.div`
  background-color: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 4px;
  padding: 8px 12px;
  margin: 4px 0;
  font-size: 14px;
`;

const SkippedEmailText = styled.span`
  color: #cf1322;
  font-weight: 500;
`;

const SkippedReasonText = styled.span`
  color: #8c8c8c;
  font-style: italic;
`;

const { Dragger } = Upload;

const InviteEmployees = () => {
  const [form] = Form.useForm();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [inviteToDelete, setInviteToDelete] = useState("");
  const [uploadResultModal, setUploadResultModal] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);

  const fetchPendingInvites = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/companies/members/pending-invites`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPendingInvites(response.data || []);
    } catch (error) {
      console.error("Error fetching pending invites:", error);
      message.error("Failed to fetch pending invites. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const sendInvitation = async (values) => {
    try {
      setLoading(true);
      const invitationResponse = await axios.post(
        `${BASE_URL}/companies/invites/add`,
        { email: values.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (invitationResponse.status === 200) {
        if (
          invitationResponse.data &&
          invitationResponse.data.status === "success"
        ) {
          message.success("Invitation successfully sent.");
          fetchPendingInvites();
          form.resetFields(["email"]);
        } else if (
          invitationResponse.data &&
          invitationResponse.data.status === "failed" &&
          invitationResponse.data.message === "Invite already done"
        ) {
          message.warning(
            "Invite for this email has already been sent. Please use a different employee email."
          );
          form.resetFields(["email"]);
        } else {
          form.resetFields(["email"]);
          message.error("An error occurred during invitation. Please try again.");
        }
      } else {
        form.resetFields(["email"]);
        message.error("An error occurred during invitation. Please try again.");
      }
    } catch (error) {
      console.error("Error sending invitation:", error);
      form.resetFields(["email"]);
      message.error("Failed to send invitation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const uploadCsvInvites = async () => {
    if (!csvFile) {
      message.error("Please select a CSV file first.");
      return;
    }

    try {
      setUploadLoading(true);
      const formData = new FormData();
      formData.append("file", csvFile);

      const response = await axios.post(
        `${BASE_URL}/companies/invites/upload_csv`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200 && response.data.status === "success") {
        setUploadResult(response.data);
        setUploadResultModal(true);
        setBulkUploadModal(false);
        setCsvFile(null);
        fetchPendingInvites();

        if (response.data.invited_count > 0) {
          message.success(
            `${response.data.invited_count} invitations sent successfully!`
          );
        }
      } else {
        message.error(
          response.data.message || "Failed to upload CSV. Please try again."
        );
      }
    } catch (error) {
      console.error("Error uploading CSV:", error);
      message.error("Failed to upload CSV. Please try again.");
    } finally {
      setUploadLoading(false);
    }
  };

  const resendInvite = async (email) => {
    try {
      setLoading(true);
      await axios.post(
        `${BASE_URL}/companies/invites/resend`,
        { email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Invitation resent successfully.");
      fetchPendingInvites();
    } catch (error) {
      console.error("Error resending invitation:", error);
      message.error("Failed to resend invitation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const deleteInvite = async () => {
    try {
      setLoading(true);
      await axios.delete(
        `${BASE_URL}/companies/invites/${inviteToDelete}/remove`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      message.success("Invite deleted successfully.");
      setShowConfirmation(false);
      fetchPendingInvites();
    } catch (error) {
      console.error("Error deleting invite:", error);
      message.error("Failed to delete invite. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (info) => {
    if (info.file.status === "removed") {
      setCsvFile(null);
      return;
    }

    const file = info.file.originFileObj || info.file;
    if (file && file.name.endsWith(".csv")) {
      setCsvFile(file);
    } else {
      message.error("Please select a valid CSV file.");
    }
  };

  const uploadProps = {
    accept: ".csv",
    multiple: false,
    maxCount: 1,
    beforeUpload: () => false,
    onChange: handleFileChange,
    onRemove: () => {
      setCsvFile(null);
    },
  };

  useEffect(() => {
    fetchPendingInvites();
  }, []);

  return (
    <DashboardStyle>
      <div className="content-container" style={{ marginLeft: "30px", padding: "20px" }}>
        <br /> <br />
        <div className="card">
          <CardHeader>Invite Employees</CardHeader>
          <div className="card-body">
            <Form form={form} onFinish={sendInvitation} layout="vertical">
              <div className="row">
                <div className="col-6">
                  <Form.Item
                    name="email"
                    rules={[{ required: true, type: "email" }]}
                  >
                    <Input placeholder="Employee Email" />
                  </Form.Item>
                </div>
                <div className="col-6">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    style={{ backgroundColor: "#3c2f61", color: "white" }}
                  >
                    Send Invite
                  </Button>
                </div>
              </div>
            </Form>

            <Divider>OR</Divider>

            <Button
              type="dashed"
              icon={<UploadOutlined />}
              onClick={() => setBulkUploadModal(true)}
              style={{ marginTop: "10px", marginBottom: "20px", borderColor: "#3c2f61", color: "#3c2f61" }}
            >
              Bulk Invite via CSV
            </Button>
          </div>

          <CardHeader>Pending Invites</CardHeader>
          <div className="card-body">
            {pendingInvites.length > 0 ? (
              <ul style={{ paddingLeft: 0 }}>
                {pendingInvites.map((invite, index) => (
                  <React.Fragment key={invite.id}>
                    <ListItem>
                      <div>
                        <i
                          className="fa fa-user"
                          style={{ marginRight: "5px", color: "#3c2f61", fontSize: "22px" }}
                        ></i>
                        {invite.user_email}
                      </div>
                      <div>
                        <InviteButton
                          onClick={() => resendInvite(invite.user_email)}
                        >
                          Resend Invite
                        </InviteButton>
                        <InviteButton
                          onClick={() => {
                            setInviteToDelete(invite.user_email);
                            setShowConfirmation(true);
                          }}
                        >
                          Cancel Invite
                        </InviteButton>
                      </div>
                    </ListItem>
                    {index < pendingInvites.length - 1 && <HorizontalLine />}
                  </React.Fragment>
                ))}
              </ul>
            ) : (
              <h6>No pending invites</h6>
            )}
          </div>
        </div>
      </div>

      <Modal
        title="Bulk Upload Employee Invites"
        open={bulkUploadModal}
        onCancel={() => setBulkUploadModal(false)}
        footer={null}
        width={600}
      >
        <p style={{ color: "#666", marginBottom: "15px" }}>
          Upload a CSV file with employee emails. The first column should contain email addresses.
        </p>

        <Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag CSV file to this area to upload</p>
          <p className="ant-upload-hint">
            Support for a single CSV file upload. Make sure the first column contains email addresses.
          </p>
        </Dragger>

        {csvFile && (
          <div style={{ marginTop: "15px" }}>
            <Button
              type="primary"
              onClick={uploadCsvInvites}
              loading={uploadLoading}
              style={{ backgroundColor: "#3c2f61", color: "white" }}
              icon={<UploadOutlined />}
            >
              Upload & Send Invites
            </Button>
          </div>
        )}
      </Modal>

      <Modal
        title="Bulk Invite Results"
        open={uploadResultModal}
        onCancel={() => setUploadResultModal(false)}
        footer={[
          <Button key="close" onClick={() => setUploadResultModal(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        {uploadResult && (
          <div>
            <Alert
              message={`Upload Complete`}
              description={`Successfully invited: ${uploadResult.invited_count} | Skipped: ${uploadResult.skipped_count}`}
              type={uploadResult.skipped_count > 0 ? "warning" : "success"}
              showIcon
              style={{ marginBottom: "20px" }}
            />

            {uploadResult.skipped_count > 0 && (
              <div>
                <h4 style={{ color: "#cf1322", marginBottom: "10px" }}>
                  Skipped Emails ({uploadResult.skipped_count}):
                </h4>
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {uploadResult.skipped.map((item, index) => (
                    <SkippedEmailItem key={index}>
                      <div>
                        <SkippedEmailText>{item.email}</SkippedEmailText>
                      </div>
                      <div>
                        <SkippedReasonText>Reason: {item.reason}</SkippedReasonText>
                      </div>
                    </SkippedEmailItem>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {showConfirmation && (
        <ConfirmationModal
          message="Are you sure you want to cancel this invite?"
          onConfirm={deleteInvite}
          onCancel={() => setShowConfirmation(false)}
        />
      )}
    </DashboardStyle>
  );
};

export default InviteEmployees;
